import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { SelectItemProps, Response } from '@ui/src';
import { accountsDictionariesApi } from './services';
import { Employee } from './types';

export interface Dictionaries {
  accounts: SelectItemProps[];
}

export interface PspAccount {
  id: string;
  description: string;
  pspPartnerId: string;
  pspPartner: {
    id: string;
    companyName: string;
    description?: string;
  };
  account?: {
    clientName: string;
    id: string;
    fullNumber: string;
  };
}

export interface DictionariesState {
  accounts: SelectItemProps[];
  isLoading: boolean;
  searchQuery: string;
  pspAccounts?: PspAccount[];
  transactionTypes?: { name: string; id: string }[];
  currentEmployee?: Employee;
  overdraftEnabled?: boolean;
}

const initialState: DictionariesState = {
  accounts: [],
  isLoading: false,
  searchQuery: '',
};

export const dictionaries = createSlice({
  name: 'dictionaries',
  initialState,
  reducers: {
    setSearchQuery: (state, action: PayloadAction<string>) => {
      state.searchQuery = action.payload;
    },
    setAccountsSettings: (
      state,
      action: PayloadAction<{ key: string; data: { value: boolean } }[]>,
    ) => {
      const overdraftSetting = action.payload?.find(
        (item) => item.key === 'OVERDRAFT-ENABLED',
      );

      if (overdraftSetting) {
        if (
          overdraftSetting.data.value === undefined ||
          overdraftSetting.data.value === null
        ) {
          state.overdraftEnabled = false;
        } else {
          state.overdraftEnabled = overdraftSetting.data.value;
        }
      }
    },
  },
  extraReducers: (builder) => {
    builder.addMatcher(
      accountsDictionariesApi.endpoints.getAccountsTransactionTypes
        .matchFulfilled,
      (
        state,
        action: PayloadAction<Response<{ name: string; id: string }>>,
      ) => {
        state.transactionTypes = action.payload?.data.edges.map(
          (item) => item.node,
        );
      },
    );
    builder.addMatcher(
      accountsDictionariesApi.endpoints.getPspAccounts.matchFulfilled,
      (state, action: PayloadAction<Response<any>>) => {
        state.pspAccounts = action.payload.data.edges.map((item) => item.node);
      },
    );
    builder.addMatcher(
      accountsDictionariesApi.endpoints.getAccountsSettings.matchFulfilled,
      (
        state,
        action: PayloadAction<
          Response<{ key: string; data: { value: boolean } }>
        >,
      ) => {
        const overdraftSetting = action.payload.data.edges?.find(
          (item) => item.node.key === 'OVERDRAFT-ENABLED',
        );

        if (overdraftSetting) {
          if (
            overdraftSetting.node.data.value === undefined ||
            overdraftSetting.node.data.value === null
          ) {
            state.overdraftEnabled = false;
          } else {
            state.overdraftEnabled = overdraftSetting.node.data.value;
          }
        }
      },
    );
    builder.addMatcher(
      accountsDictionariesApi.endpoints.getAccountsEmployee.matchFulfilled,
      (state, action: PayloadAction<{ data: Employee }>) => {
        state.currentEmployee = action.payload.data;
      },
    );
  },
});

export const { setSearchQuery, setAccountsSettings } = dictionaries.actions;

export default dictionaries.reducer;
